import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { pathByRouteName } from '../../util/routes';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import Modal from '../Modal/Modal';
import reviewIllustration from '../../assets/bag-check-white.png';
import { fetchAvailabilityReviews, reviewAvailability } from './listingAvailability';
import css from './ListingAvailabilityReminder.module.css';

const MODAL_ID = 'ListingAvailabilityReminder';
const sessionKey = userId => `listing-availability-dismissed:${userId}`;
const isDismissed = userId => {
  try {
    return window.sessionStorage.getItem(sessionKey(userId)) === 'true';
  } catch (e) {
    return false;
  }
};

const ReviewIllustration = () => (
  <img className={css.introIllustration} src={reviewIllustration} alt="" />
);

// Remount for each listing/image so the browser cannot display the previous photo
// while the next one downloads. Keep the space reserved to avoid layout shifts.
const ReviewListingImage = ({ src, title }) => {
  const [status, setStatus] = useState('loading');
  const imageRef = useRef(null);

  useEffect(() => {
    const image = imageRef.current;
    if (image?.complete) setStatus(image.naturalWidth > 0 ? 'loaded' : 'error');
  }, []);

  return (
    <div className={css.imageFrame}>
      {status !== 'error' ? (
        <img
          ref={imageRef}
          className={`${css.image} ${status === 'loading' ? css.imageLoading : ''}`}
          src={src}
          alt={title}
          onLoad={() => setStatus('loaded')}
          onError={() => setStatus('error')}
        />
      ) : null}
      {status === 'loading' ? (
        <div className={css.imageStatus} role="status">
          <span className={css.spinner} aria-hidden="true" />
          <FormattedMessage id="ListingAvailabilityReminder.imageLoading" />
        </div>
      ) : status === 'error' ? (
        <div className={css.imageStatus} role="status">
          <FormattedMessage id="ListingAvailabilityReminder.imageUnavailable" />
        </div>
      ) : null}
    </div>
  );
};

export const AvailabilityReview = ({ userId }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const routes = useRouteConfiguration();
  const [removedCount, setRemovedCount] = useState(0);
  const [listings, setListings] = useState([]);
  const [index, setIndex] = useState(0);
  const [reviewStarted, setReviewStarted] = useState(false);
  const reviewFocusTarget = useRef(null);
  const complete = reviewStarted && listings.length > 0 && index === listings.length;
  const [dismissed, setDismissed] = useState(() => isDismissed(userId));
  const [pendingAction, setPendingAction] = useState(null);
  const busy = pendingAction !== null;
  const [error, setError] = useState(false);
  const saving = useRef(false);
  const mounted = useRef(true);
  const otherModalOpen = useSelector(state =>
    state.ui.disableScrollRequests.some(r => r.componentId !== MODAL_ID && r.disableScrolling)
  );

  useEffect(() => {
    if (reviewStarted) reviewFocusTarget.current?.focus();
  }, [reviewStarted, complete]);

  useEffect(() => {
    mounted.current = true;
    if (!dismissed) {
      dispatch(fetchAvailabilityReviews()).then(
        result => mounted.current && setListings(result),
        () => {} // A failed background query must not interrupt sign-in or mark items reviewed.
      );
    }
    return () => {
      mounted.current = false;
    };
  }, [dispatch, userId, dismissed]);

  const dismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(sessionKey(userId), 'true');
    } catch (e) {
      // In-memory dismissal still works when browser storage is unavailable.
    }
  };

  const review = async keepSelling => {
    if (saving.current) return;
    saving.current = true;
    setPendingAction(keepSelling ? 'keep' : 'remove');
    setError(false);
    try {
      const response = await dispatch(reviewAvailability(listings[index].id, keepSelling));
      if (mounted.current) {
        if (!keepSelling && response.data.data.attributes.state === 'closed') {
          setRemovedCount(value => value + 1);
        }
        setIndex(value => value + 1);
      }
    } catch (e) {
      if (mounted.current) setError(true);
    } finally {
      saving.current = false;
      if (mounted.current) setPendingAction(null);
    }
  };

  const listing = listings[index];
  const { title, price, publicData = {} } = listing?.attributes || {};
  const language = intl.locale.split('-')[0];
  const localizedTitle = publicData[`${language}_title`] || title;
  const brand = publicData[`${language}_designerLabel`] || publicData.designer;
  const variants = listing?.images?.[0]?.attributes?.variants || {};
  const image = variants['scaled-large'] || variants['scaled-medium'];

  return (
    <Modal
      id={MODAL_ID}
      isOpen={(!!listing || complete) && !dismissed && !otherModalOpen}
      onClose={dismiss}
      onManageDisableScrolling={(id, value) => dispatch(manageDisableScrolling(id, value))}
      usePortal
      dontShowClose
      closeButtonClassName={css.closeButton}
      className={css.modal}
      containerClassName={`${css.container} ${
        !reviewStarted || complete ? css.introContainer : ''
      }`}
      scrollLayerClassName={css.overlay}
    >
      {complete ? (
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="availability-complete-title"
          aria-describedby="availability-complete-description"
        >
          <ReviewIllustration />
          <h2
            id="availability-complete-title"
            className={css.introHeading}
            ref={reviewFocusTarget}
            tabIndex={-1}
          >
            <FormattedMessage id="ListingAvailabilityReminder.completeTitle" />
          </h2>
          <p id="availability-complete-description" className={css.introDescription}>
            <FormattedMessage id="ListingAvailabilityReminder.completeDescription" />
          </p>
          <p className={css.summary}>
            <FormattedMessage
              id="ListingAvailabilityReminder.removedCount"
              values={{ removed: removedCount, total: listings.length }}
            />
          </p>
          <Link
            className={`${css.keep} ${css.viewListings}`}
            to={pathByRouteName('ManageListingsPage', routes)}
            onClick={dismiss}
          >
            <FormattedMessage id="ListingAvailabilityReminder.viewListings" />
          </Link>
        </section>
      ) : listing && !reviewStarted ? (
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="availability-intro-title"
          aria-describedby="availability-intro-description"
        >
          <ReviewIllustration />
          <h2 id="availability-intro-title" className={css.introHeading}>
            <FormattedMessage id="ListingAvailabilityReminder.introTitle" />
          </h2>
          <p id="availability-intro-description" className={css.introDescription}>
            <FormattedMessage id="ListingAvailabilityReminder.introDescription" />
          </p>
          <button type="button" className={css.keep} onClick={() => setReviewStarted(true)}>
            <FormattedMessage id="ListingAvailabilityReminder.startReview" />
          </button>
          <button type="button" className={css.later} onClick={dismiss}>
            <FormattedMessage id="ListingAvailabilityReminder.maybeLater" />
          </button>
        </section>
      ) : listing ? (
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="availability-title"
          aria-busy={busy}
        >
          <p className={css.progress} aria-live="polite">
            <FormattedMessage
              id="ListingAvailabilityReminder.progress"
              values={{ current: index + 1, total: listings.length }}
            />
          </p>
          {image ? (
            <ReviewListingImage
              key={`${listing.id.uuid}:${image.url}`}
              src={image.url}
              title={localizedTitle}
            />
          ) : (
            <div className={css.placeholder} />
          )}
          {brand ? <div className={css.brand}>{brand}</div> : null}
          <p id="availability-title" className={css.title}>
            {localizedTitle}
          </p>
          {price ? <p className={css.price}>{formatMoney(intl, price)}</p> : null}
          {error ? (
            <p role="alert" className={css.error}>
              <FormattedMessage id="ListingAvailabilityReminder.error" />
            </p>
          ) : null}
          <button
            ref={reviewFocusTarget}
            className={css.keep}
            disabled={busy}
            onClick={() => review(true)}
          >
            {pendingAction === 'keep' ? (
              <span className={css.loading} role="status">
                <span className={css.spinner} aria-hidden="true" />
                <FormattedMessage id="ListingAvailabilityReminder.saving" />
              </span>
            ) : (
              <FormattedMessage id="ListingAvailabilityReminder.keepSelling" />
            )}
          </button>
          <button className={css.remove} disabled={busy} onClick={() => review(false)}>
            {pendingAction === 'remove' ? (
              <span className={css.loading} role="status">
                <span className={css.spinner} aria-hidden="true" />
                <FormattedMessage id="ListingAvailabilityReminder.removing" />
              </span>
            ) : (
              <FormattedMessage id="ListingAvailabilityReminder.remove" />
            )}
          </button>
        </section>
      ) : null}
    </Modal>
  );
};

export default function ListingAvailabilityReminder() {
  const userId = useSelector(state =>
    state.auth.isAuthenticated ? state.user.currentUser?.id?.uuid : null
  );
  return userId ? <AvailabilityReview key={userId} userId={userId} /> : null;
}
